/**
 * 🌍 useActiveHoliday Hook
 *
 * Determines which holiday (if any) should be displayed based on current date.
 * Returns highest priority holiday when multiple holidays overlap.
 *
 * Respects environment variable:
 * - REACT_APP_HOLIDAY_GREETINGS_ENABLED=false → Always returns null
 * - REACT_APP_HOLIDAY_GREETINGS_ENABLED=true → Shows holidays (date-based)
 * - Not set → Auto-detect based on date (default behavior)
 */
import { useMemo } from 'react';
import { HOLIDAYS } from '../config/holidays';
/**
 * Get the active holiday for the current date
 */
export var useActiveHoliday = function (options) {
    if (options === void 0) { options = {}; }
    var _a = options.currentDate, currentDate = _a === void 0 ? new Date() : _a, _b = options.forceDisplay, forceDisplay = _b === void 0 ? false : _b;
    var activeHoliday = useMemo(function () {
        // Check environment variable (supports both new and legacy names)
        var envEnabled = process.env.REACT_APP_HOLIDAY_GREETINGS_ENABLED || process.env.REACT_APP_CHRISTMAS_THEME;
        // If explicitly disabled via env, return null
        if (envEnabled === 'false' && !forceDisplay) {
            return null;
        }
        if (forceDisplay) {
            return HOLIDAYS[0]; // Return first holiday for forced display
        }
        var month = currentDate.getMonth(); // 0-indexed
        var day = currentDate.getDate();
        var year = currentDate.getFullYear();
        // Find all holidays that match current date
        var matchingHolidays = HOLIDAYS.filter(function (holiday) {
            return holiday.dateRanges.some(function (range) {
                // If year is specified, must match exactly
                if (range.year !== undefined && range.year !== year) {
                    return false;
                }
                // Check if current date falls within range
                if (range.month !== month) {
                    return false;
                }
                return day >= range.startDay && day <= range.endDay;
            });
        });
        // Return highest priority holiday if multiple match
        if (matchingHolidays.length > 0) {
            return matchingHolidays.reduce(function (prev, current) {
                return current.priority > prev.priority ? current : prev;
            });
        }
        return null;
    }, [currentDate, forceDisplay]);
    return activeHoliday;
};
